import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Tuple of key sets, one per partition, listing the keys of `T` each gates on.
 */
export type Partitions<T> = readonly (readonly (keyof T)[])[];

/**
 * Tuple of slice observables returned by {@link partitionByKeys}: each element
 * is `Observable<Pick<T, K>>` for that partition's keys.
 */
export type PartitionedStreams<T, P extends Partitions<T>> = {
  [I in keyof P]: Observable<Pick<T, P[I][number]>>;
};

const assertDisjoint = <T extends object>(partitions: Partitions<T>): void => {
  const seenIn = new Map<keyof T, number>();
  partitions.forEach((keys, partitionIndex) => {
    keys.forEach(key => {
      const previousPartitionIndex = seenIn.get(key);
      if (previousPartitionIndex !== undefined) {
        throw new Error(
          `partitionByKeys: key "${String(key)}" appears in partitions ${previousPartitionIndex} and ${partitionIndex}; partitions must be disjoint.`
        );
      }
      seenIn.set(key, partitionIndex);
    });
  });
};

const pickKeys = <T extends object, K extends keyof T>(
  value: T,
  keys: readonly K[]
): Pick<T, K> => {
  const slice = {} as Pick<T, K>;
  for (const key of keys) {
    slice[key] = value[key];
  }
  return slice;
};

/**
 * Split an observable of a record into N sub-observables, each gated by a
 * disjoint subset of the source's keys. A partition emits only when one of
 * its keys changes (top-level reference equality) and emits a `Pick<T, K>`
 * slice — non-gated keys are unreachable, so stale reads are ruled out by
 * typing, not convention.
 *
 * Disjointness is enforced at construction; a key in two partitions throws.
 * Keys absent from every partition are leftovers: their changes trigger
 * nothing, and no slice exposes them.
 *
 * To read across partitions at one of your own emissions, compose slices
 * via `combineLatest` (each is fresh in its gated keys) or pull the full
 * source via `withLatestFrom(source$)`.
 *
 * Each partition subscribes to `source$` independently; the operator does
 * not multicast. Assumes a hot source — cold or expensive sources should
 * be `share`d upstream.
 *
 * @example
 * ```ts
 * const [sourceDataSettings$, renderingSettings$, uiState$] = partitionByKeys(
 *   viewSettings$,
 *   [
 *     ['useNewGrouping', 'activeDiffMode'],
 *     ['collapsedGroupIds', 'bundleRelationships', 'highlightDisconnectedComponents'],
 *     ['isLegendActive'],
 *   ]
 * );
 * ```
 */
export const partitionByKeys = <
  T extends object,
  const P extends Partitions<T>,
>(
  source$: Observable<T>,
  partitions: P
): PartitionedStreams<T, P> => {
  assertDisjoint<T>(partitions);
  // `Array.prototype.map` returns `U[]`, losing the tuple shape we need.
  // The assertion is isolated to one place; runtime shape matches `P` by construction.
  return partitions.map(keys =>
    source$.pipe(
      distinctUntilChanged((prev, curr) =>
        keys.every(k => prev[k] === curr[k])
      ),
      map(value => pickKeys(value, keys))
    )
  ) as PartitionedStreams<T, P>;
};
